import { type ReactNode, useEffect } from 'react';
import {
  data,
  isRouteErrorResponse,
  type LinkDescriptor,
  Links,
  type MiddlewareFunction,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useParams,
} from 'react-router';

import type { Route } from './+types/root';
import { apiIsomorphicFetchUserEnvironment } from './api/userEnvironment/apiFetchUserEnvironment';
import { App } from './app';
import { getAppConstants } from './config/appConstants';
import { serverContextMiddleware } from './horizon/middlewares/server-context-middleware';
import { getServerRouterContext } from './horizon/router-contexts/server-router-context';
import { HorizonBrowserNotSupportedScript } from './horizon/scripts/HorizonBrowserNotSupportedScript';
import { HorizonGoogleMapsScripts } from './horizon/scripts/HorizonGoogleMapsScripts';
import { HorizonGoogleTagManagerNoScript } from './horizon/scripts/HorizonGoogleTagManagerNoScript';
import { HorizonGoogleTagManagerScript } from './horizon/scripts/HorizonGoogleTagManagerScript';
import { HorizonMixpanelScript } from './horizon/scripts/HorizonMixpanelScript';
import { HorizonPolyfillScript } from './horizon/scripts/HorizonPolyfillScript';
import { HorizonSetIdealistObjectScript } from './horizon/scripts/HorizonSetIdealistObjectScript';
import { HorizonTrackJsScript } from './horizon/scripts/HorizonTrackJsScript';
import { getMatchesOverrideLocale } from './horizon/utils/getMatchesOverrideLocale';
import { getI18n } from './i18n/getI18n';
import type { HorizonLoaderFunctionArgs } from './routing/types/HorizonLoaderFunctionArgs';
import type { HorizonLoaderFunctionData } from './routing/types/HorizonLoaderFunctionData';
import { log } from './utils/logging';
import { trackError } from './utils/trackError';

import themeGlobalCss from './theme/theme-global.css?raw';

export const middleware: MiddlewareFunction[] = [serverContextMiddleware];

export async function loader({
  request,
  context,
}: HorizonLoaderFunctionArgs<void>) {
  const { serverContext } = getServerRouterContext(context);

  const { user, environment, appConfig } =
    await apiIsomorphicFetchUserEnvironment({
      request,
      context,
    })();

  const loaderData = {
    user,
    environment,
    appConfig,
    contextLocale: serverContext.locale,
    flash: serverContext.flash,
    settings: {
      siteVerification: serverContext.horizonServerEnv.googleSiteVerification,
      tagManager: serverContext.horizonServerEnv.googleTagManager
        ? {
            trackingId:
              serverContext.horizonServerEnv.googleTagManager.trackingId,
          }
        : null,
      mixpanel: serverContext.horizonServerEnv.mixpanel,
      trackJs: serverContext.horizonServerEnv.trackJs
        ? {
            token: serverContext.horizonServerEnv.trackJs.token,
            application: serverContext.horizonServerEnv.trackJs.application,
            version: serverContext.horizonServerEnv.trackJs.version,
          }
        : null,
      maps:
        (serverContext.horizonServerEnv.googleMaps.enabled ||
          serverContext.hasUseRealGMapsGoogle) &&
        serverContext.horizonServerEnv.googleMaps.apiKey
          ? { apiKey: serverContext.horizonServerEnv.googleMaps.apiKey }
          : null,

      rscEnabled: serverContext.horizonServerEnv.rscEnabled,
    },
    appConstants: getAppConstants(),
  };

  if (!serverContext.localeCookieHeader) return loaderData;

  return data(loaderData, {
    headers: {
      'Set-Cookie': serverContext.localeCookieHeader,
    },
  });
}

export function shouldRevalidate() {
  return false;
}

export function links(): LinkDescriptor[] {
  return [
    {
      rel: 'preload',
      href: `/static/fonts/source-sans-3/source-sans-3-latin-wght-normal.woff2`,
      as: 'font',
      type: 'font/woff',
      crossOrigin: 'anonymous',
    },
  ];
}

export function ServerLayout({ children }: { children: ReactNode }) {
  // RSC interop
  const layoutProps = (() => {
    const childrenProps =
      // oxlint-disable-next-line typescript/no-explicit-any
      (children as any)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .props as Route.ServerComponentProps;

    return childrenProps.loaderData
      ? childrenProps
      : {
          loaderData: useLoaderData<HorizonLoaderFunctionData<typeof loader>>(),
          matches: useMatches(),
          params: useParams(),
        };
  })();

  const { user, environment, appConfig, contextLocale, settings } =
    layoutProps.loaderData;

  const { overrideLocale } = getMatchesOverrideLocale({
    matches: layoutProps.matches,
  });
  const locale = overrideLocale ?? contextLocale;

  const { i18n } = getI18n(locale);
  const appName = i18n('Idealist');

  const isInEmbedSearchWidget = Boolean(layoutProps.params.embedSearchWidgetId);

  return (
    <html
      lang={locale}
      // Auto-translate can crash the app with the error
      // Failed to execute 'removeChild' on 'Node': The node to be removed is not a child
      translate="no"
      className="notranslate"
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, minimum-scale=1, shrink-to-fit=no"
        />
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />

        <meta
          name="google"
          // Additional layer to ask browser and search engines
          // to no auto-translate the website because it can crash React
          content="notranslate"
        />

        {settings.siteVerification && (
          <meta
            name="google-site-verification"
            content={settings.siteVerification}
          />
        )}

        {/* Favicons & app meta */}
        <link rel="manifest" href="/manifest.json" />
        <meta name="apple-mobile-web-app-title" content={appName} />
        <meta name="application-name" content={appName} />
        <meta name="theme-color" content="#ffffff" />

        <link rel="shortcut icon" href="/favicon.ico" />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#0275d8" />

        {/* Styles & links */}
        <style>{themeGlobalCss}</style>
        <Links />

        {/* Polyfill & prepare environment */}
        <HorizonPolyfillScript />
        <HorizonSetIdealistObjectScript
          locale={locale}
          user={user}
          environment={environment}
          appConfig={appConfig}
          appConstants={layoutProps.loaderData.appConstants}
        />
        <HorizonBrowserNotSupportedScript locale={locale} />

        {/* Tracking */}
        {settings.trackJs && (
          <HorizonTrackJsScript
            token={settings.trackJs.token}
            application={settings.trackJs.application}
            version={settings.trackJs.version}
          />
        )}

        {settings.mixpanel && (
          <HorizonMixpanelScript
            token={settings.mixpanel.token}
            debug={settings.mixpanel.debug}
          />
        )}

        {settings.tagManager && (
          <HorizonGoogleTagManagerScript
            trackingId={settings.tagManager.trackingId}
          />
        )}

        {/* Additional libs */}
        <HorizonGoogleMapsScripts apiKey={settings.maps?.apiKey || null} />
      </head>
      <body>
        {settings.tagManager && (
          <HorizonGoogleTagManagerNoScript
            trackingId={settings.tagManager.trackingId}
          />
        )}
        {children}

        {!isInEmbedSearchWidget && <ScrollRestoration />}
        {!settings.rscEnabled && <Scripts />}
      </body>
    </html>
  );
}

export function ServerComponent({
  loaderData: { user, environment, appConfig, contextLocale, flash },
  matches,
}: Route.ServerComponentProps) {
  const { overrideLocale } = getMatchesOverrideLocale({ matches });
  const locale = overrideLocale ?? contextLocale;

  return (
    <App
      appConfig={appConfig}
      flash={flash}
      locale={locale}
      user={user}
      environment={environment}
    />
  );
}

function Error500WithTrace({ error }: { error: Error }) {
  useEffect(() => {
    log.error(`stacktrace:\n${error.stack}`);
    trackError(error);
  }, [error]);

  return (
    <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
      <h1>Something went wrong</h1>

      <p>
        We're sorry, something went wrong. Please try again and if the problem
        persists, please contact us via{' '}
        <a href="https://www.idealist.org/en/about/contact">this form</a> or at{' '}
        <a href="mailto:support@idealist.org">support@idealist.org</a>.
      </p>

      <footer>
        <a href="/web/static">Go back to the site</a>
      </footer>
    </div>
  );
}

export function ErrorBoundary({ error }: { error: Error }) {
  if (import.meta.env.DEV && error && error instanceof Error) {
    return (
      <>
        <h1>Error</h1>
        <p>{error.message}</p>

        {error.stack && (
          <pre className="w-full p-4 overflow-x-auto">
            <code>{error.stack}</code>
          </pre>
        )}
      </>
    );
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h1>404</h1>
          <p>The requested page could not be found.</p>
        </div>
      );
    }
  }

  return <Error500WithTrace error={error} />;
}
